import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { useAccessStore } from "@/stores/accessStore";
import { useAppLoaderStore } from "@/stores/appLoaderStore";
import { useSessionLockStore } from "@/stores/sessionLockStore";
import { isIdleLockEnabled } from "@/services/idleLockService";
import { isErrorPageRoute } from "@/utils/homeRoute";
import {
  isAdminHost,
  isCentralHost,
  isTenancyFrontendEnabled,
  isTenantHost,
} from "@/utils/hostname";
import { verifyCurrentTenantWorkspace } from "@/utils/workspaceLookup";
import {
  canAccessCrmRoute,
  resolveCrmLanding,
} from "@/utils/crmAccess";
import {
  canAccessFinanceAccountingRoute,
  canAccessFinanceSalesRoute,
  matchFinanceAccountingRoute,
  matchFinanceSalesRoute,
} from "@/utils/financeAccess";
import {
  canAccessAdminAssetsRoute,
  canAccessAdminReportsRoute,
  canAccessAdminSettingsRoute,
  matchAdminAssetsRoute,
  matchAdminReportsRoute,
  matchAdminSettingsRoute,
} from "@/utils/adminAccess";
import { canAccessHrmRoute } from "@/utils/hrmAccess";
import dashboardIndex from "@/views/pages/dashboard/dashboard-index.vue";
import adminDashboard from "@/views/pages/dashboard/admin-dashboard/admin-dashboard.vue";
import employeeDashboard from "@/views/pages/dashboard/employee-dashboard/employee-dashboard.vue";
import employeeMyDocuments from "@/views/pages/dashboard/employee-dashboard/employee-my-documents.vue";
import dealsDashboard from "@/views/pages/dashboard/deals-dashboard/deals-dashboard.vue";
import leadsDashboard from "@/views/pages/dashboard/leads-dashboard/leads-dashboard.vue";
import applicationIndex from "@/views/pages/applications/application-index.vue";
import chatIndex from "@/views/pages/applications/chat-index.vue";
import voiceCall from "@/views/pages/applications/calls/voice-call.vue";
import videoCall from "@/views/pages/applications/calls/video-call.vue";
import outgoingCall from "@/views/pages/applications/calls/outgoing-call.vue";
import incomingCall from "@/views/pages/applications/calls/incoming-call.vue";
import callHistory from "@/views/pages/applications/calls/call-history.vue";
import calendarIndex from "@/views/pages/applications/calendar-index.vue";
import emailIndex from "@/views/pages/applications/email-index.vue";
import emailReply from "@/views/pages/applications/email-reply.vue";
import notificationsPage from "@/views/pages/applications/notifications-page.vue";
import superAdmin from "@/views/pages/superadmin/super-admin.vue";
import superDashboard from "@/views/pages/superadmin/super-dashboard/super-dashboard.vue";
import companiesList from "@/views/pages/superadmin/companies/companies-list.vue";
import companiesDetails from "@/views/pages/superadmin/companies/companies-details.vue";
import CrmCompaniesList from "@/views/pages/crm/companies/companies-list.vue";
import CrmCompanyDetails from "@/views/pages/crm/companies/company-details.vue";
import superSubscription from "@/views/pages/superadmin/super-subscription.vue";
import clientsGrid from "@/views/pages/crm/clients/clients-grid.vue";
import clientsList from "@/views/pages/crm/clients/clients-list.vue";
import projectsIndex from "@/views/pages/projects/projects-index.vue";
import clientsDetails from "@/views/pages/crm/clients/clients-details.vue";
import projectGrid from "@/views/pages/projects/project/project-grid.vue";
import projectList from "@/views/pages/projects/project/project-list.vue";
import projectDetails from "@/views/pages/projects/project/project-details.vue";
import todoList from "@/views/pages/applications/todo-list.vue";
import todoIndex from "@/views/pages/applications/todo-index.vue";
import crmIndex from "@/views/pages/crm/crm-index.vue";
import dealsList from "@/views/pages/crm/deals/deals-list.vue";
import dealsGrid from "@/views/pages/crm/deals/deals-grid.vue";
import dealsDetails from "@/views/pages/crm/deals/deals-details.vue";
import leadsDetails from "@/views/pages/crm/leads/leads-details.vue";
import leadsGrid from "@/views/pages/crm/leads/leads-grid.vue";
import leadsList from "@/views/pages/crm/leads/leads-list.vue";
import employeesIndex from "@/views/pages/hrm/employees/employees-index.vue";
import employeesList from "@/views/pages/hrm/employees/employees-list.vue";
import employeesGrid from "@/views/pages/hrm/employees/employees-grid.vue";
import employeeDetails from "@/views/pages/hrm/employees/employee-details.vue";
import employeeDepartment from "@/views/pages/hrm/employees/employee-department.vue";
import employeeSections from "@/views/pages/hrm/employees/employee-sections.vue";
import employeeDesignations from "@/views/pages/hrm/employees/employee-designations.vue";
import employeePolicy from "@/views/pages/hrm/employees/employee-policy.vue";
import employeeOrgChart from "@/views/pages/hrm/employees/employee-org-chart.vue";
import ticketList from "@/views/pages/hrm/tickets/tickets-list.vue";
import ticketsIndex from "@/views/pages/hrm/tickets/tickets-index.vue";
import ticketsGrid from "@/views/pages/hrm/tickets/tickets-grid.vue";
import ticketsDetails from "@/views/pages/hrm/tickets/tickets-details.vue";
import accountingIndex from "@/views/pages/finance-accounts/accounting/accounting-index.vue";
import budgetsIndex from "@/views/pages/finance-accounts/accounting/budgets-index.vue";
import budgetsExpenses from "@/views/pages/finance-accounts/accounting/budgets-expenses.vue";
import budgetsRevenues from "@/views/pages/finance-accounts/accounting/budgets-revenues.vue";
import payrollIndex from "@/views/pages/finance-accounts/payroll/payroll-index.vue";
import employeeSalary from "@/views/pages/finance-accounts/payroll/employee-salary.vue";
import mypayslipIndex from "@/views/pages/hrm/employees/my-employee-payslips.vue";
import myp9Index from "@/views/pages/hrm/employees/my-employee-p9.vue";
import payrollAdditions from "@/views/pages/finance-accounts/payroll/payroll-additions.vue";
import payrollOvertime from "@/views/pages/finance-accounts/payroll/payroll-overtime.vue";
import payrollOvertimeEntries from "@/views/pages/finance-accounts/payroll/payroll-overtime-entries.vue";
import payrollDeduction from "@/views/pages/finance-accounts/payroll/payroll-deduction.vue";
import payrollPeriodAdjustments from "@/views/pages/finance-accounts/payroll/payroll-period-adjustments.vue";
import payrollSettings from "@/views/pages/finance-accounts/payroll/payroll-settings.vue";
import payrollBankTransferTemplates from "@/views/pages/finance-accounts/payroll/payroll-bank-transfer-templates.vue";
import payrollMetaConversion from "@/views/pages/finance-accounts/payroll/payroll-meta-conversion.vue";
import adminPayrollPresets from "@/views/pages/finance-accounts/payroll/super-payroll-presets.vue";
import assetsIndex from "@/views/pages/administration/assets/assets-index.vue";
import assetsList from "@/views/pages/administration/assets/assets-list.vue";
import assetsCategories from "@/views/pages/administration/assets/assets-categories.vue";
// import helpSupports from "@/views/pages/administration/supports/help-supports.vue";
// import knowledgebaseIndex from "@/views/pages/administration/supports/knowledgebase-index.vue";
// import knowledgebaseView from "@/views/pages/administration/supports/knowledgebase-view.vue";
// import knowledgebaseDetails from "@/views/pages/administration/supports/knowledgebase-details.vue";
import userManagement from "@/views/pages/administration/user-management/user-management.vue";
import userList from "@/views/pages/administration/user-management/user-list.vue";
import rolesPermissions from "@/views/pages/administration/user-management/roles-permission.vue";
import rolePermissionsEditor from "@/views/pages/administration/user-management/role-permissions-editor.vue";
import permissionIndex from "@/views/pages/administration/user-management/permission-index.vue";
import generalSettings from "@/views/pages/administration/settings/general-settings/general-settings.vue";
import profileSettings from "@/views/pages/administration/settings/general-settings/profile-settings.vue";
import securitySettings from "@/views/pages/administration/settings/general-settings/security-settings.vue";
import websiteSettings from "@/views/pages/administration/settings/website-settings/website-settings.vue";
import bussinessSettings from "@/views/pages/administration/settings/website-settings/bussiness-settings.vue";
import seoSettings from "@/views/pages/administration/settings/website-settings/seo-settings.vue";
import localizationSettings from "@/views/pages/administration/settings/website-settings/localization-settings.vue";
import prefixes from "@/views/pages/administration/settings/website-settings/prefixes-settings.vue";
import preferences from "@/views/pages/administration/settings/website-settings/preferences-settings.vue";
import appearance from "@/views/pages/administration/settings/website-settings/appearance-settings.vue";
import language from "@/views/pages/administration/settings/website-settings/language-settings.vue";
import languageWeb from "@/views/pages/administration/settings/website-settings/language-web.vue";
import addLanguage from "@/views/pages/administration/settings/website-settings/add-language.vue";
import authenticationSettings from "@/views/pages/administration/settings/website-settings/authentication-settings.vue";
import aiSettings from "@/views/pages/administration/settings/website-settings/ai-settings.vue";
import appSettings from "@/views/pages/administration/settings/app-settings/app-settings.vue";
import salarySettings from "@/views/pages/administration/settings/app-settings/salary-settings.vue";
import approvalSettings from "@/views/pages/administration/settings/app-settings/approval-settings.vue";
import invoiceSettings from "@/views/pages/administration/settings/app-settings/invoice-settings.vue";
import customFields from "@/views/pages/administration/settings/app-settings/custom-fields.vue";
import systemSettings from "@/views/pages/administration/settings/system-settings/system-settings.vue";
import emailSettings from "@/views/pages/administration/settings/system-settings/email-settings.vue";
import emailTemplate from "@/views/pages/administration/settings/system-settings/email-template.vue";
import smsSettings from "@/views/pages/administration/settings/system-settings/sms-settings.vue";
import smsTemplate from "@/views/pages/administration/settings/system-settings/sms-template.vue";
import otpSettings from "@/views/pages/administration/settings/system-settings/otp-settings.vue";
import gdprSettings from "@/views/pages/administration/settings/system-settings/gdpr-settings.vue";
import maintenanceMode from "@/views/pages/administration/settings/system-settings/maintenance-mode.vue";
import financialSettings from "@/views/pages/administration/settings/financial-settings/financial-settings.vue";
import paymentGateway from "@/views/pages/administration/settings/financial-settings/payment-gateway.vue";
import taxRates from "@/views/pages/administration/settings/financial-settings/tax-rates.vue";
import kenyaTaxCompliance from "@/views/pages/administration/settings/financial-settings/kenya-tax-compliance.vue";
import currenciesSettings from "@/views/pages/administration/settings/financial-settings/currencies-settings.vue";
import customCss from "@/views/pages/administration/settings/others-settings/custom-css.vue";
import customJs from "@/views/pages/administration/settings/others-settings/custom-js.vue";
import othersSettings from "@/views/pages/administration/settings/others-settings/others-settings.vue";
import cronJob from "@/views/pages/administration/settings/others-settings/cronjob-settings.vue";
import cronjobSchedule from "@/views/pages/administration/settings/others-settings/cronjob-schedule.vue";
import storageSettings from "@/views/pages/administration/settings/others-settings/storage-setings.vue";
import banIpAddress from "@/views/pages/administration/settings/others-settings/ban-ip-address.vue";
import backupSettings from "@/views/pages/administration/settings/others-settings/backup-settings.vue";
import clearCache from "@/views/pages/administration/settings/others-settings/clear-cache.vue";
import migrationCenter from "@/views/pages/administration/settings/others-settings/migration-center.vue";
import notesIndex from "@/views/pages/applications/notes-index.vue";
import socialFeed from "@/views/pages/applications/social-feed.vue";
import invoicesApp from "@/views/pages/applications/invoices-app.vue";
import addInvoices from "@/views/pages/applications/add-invoices.vue";
import editInvoices from "@/views/pages/applications/edit-invoices.vue";
import invoiceDetails from "@/views/pages/applications/invoice-details.vue";
import fileManager from "@/views/pages/applications/file-manager/file-manager.vue";
import kanbanView from "@/views/pages/applications/kanban-view/kanban-view.vue";
import pagesIndex from "@/views/pages/pages/pages-index.vue";
import starterIndex from "@/views/pages/pages/starter-index.vue";
import profileIndex from "@/views/pages/pages/profile-index.vue";
import companyProfileIndex from "@/views/pages/superadmin/companies/companies-details.vue";
import galleryIndex from "@/views/pages/pages/gallery-index.vue";
import searchResult from "@/views/pages/pages/search-result.vue";
import timelineIndex from "@/views/pages/pages/timeline-index.vue";
import pricingIndex from "@/views/pages/pages/pricing-index.vue";
import comingSoon from "@/views/pages/pages/coming-soon.vue";
import underMaintenance from "@/views/pages/pages/under-maintenance.vue";
import underConstruction from "@/views/pages/pages/under-construction.vue";
import apiKeys from "@/views/pages/pages/api-keys.vue";
import privacyPolicy from "@/views/pages/pages/privacy-policy.vue";
import termsCondition from "@/views/pages/pages/terms-condition.vue";

import layoutIndex from "@/views/pages/layout/layout-index.vue";
import layoutHorizontal from "@/views/pages/layout/layout-horizontal.vue";
import layoutDetached from "@/views/pages/layout/layout-detached.vue";
import layoutModern from "@/views/pages/layout/layout-modern.vue";
import layoutTwocolum from "@/views/pages/layout/layout-two-column.vue";
import layoutHovered from "@/views/pages/layout/layout-hovered.vue";
import layoutBox from "@/views/pages/layout/layout-box.vue";
import layoutHorizontalSingle from "@/views/pages/layout/layout-horizontal-single.vue";
import layoutHorizontalOverlay from "@/views/pages/layout/layout-horizontal-overlay.vue";
import layoutHorizontalBox from "@/views/pages/layout/layout-horizontal-box.vue";
import layoutHorizontalSidemenu from "@/views/pages/layout/layout-horizontal-sidemenu.vue";
import layoutVerticalTransparent from "@/views/pages/layout/layout-vertical-transparent.vue";
import layoutWithoutHeader from "@/views/pages/layout/layout-without-header.vue";
import layoutRtl from "@/views/pages/layout/layout-rtl/layout-rtl.vue";
import layoutDark from "@/views/pages/layout/layout-dark.vue";

import Tables from "@/views/pages/uiinterface/tables/ui-tables";
import Tables_Basic from "@/views/pages/uiinterface/tables/tables-basic.vue";
import Data_Tables from "@/views/pages/uiinterface/tables/data-tables.vue";
import Chartapex from "@/views/pages/uiinterface/charts/apex/chart-apex.vue";
import Chartc3 from "@/views/pages/uiinterface/charts/c3/chart-c3.vue";
import Chartflot from "@/views/pages/uiinterface/charts/flot/chart-flot.vue";
import Chartjs from "@/views/pages/uiinterface/charts/js/chart-js.vue";
import Chartmorris from "@/views/pages/uiinterface/charts/morris/chart-morris.vue";
import Charts from "@/views/pages/uiinterface/charts/ui-charts.vue";
import Forms from "@/views/pages/uiinterface/forms/ui-forms.vue";
import Formbasicinput from "@/views/pages/uiinterface/forms/form-elements/formbasic/formbasic-input.vue";
import Formcheckboxradios from "@/views/pages/uiinterface/forms/form-elements/form-checkbox-radios.vue";
import Formgridgutters from "@/views/pages/uiinterface/forms/form-elements/form-grid-gutters.vue";
import Formselect from "@/views/pages/uiinterface/forms/form-elements/form-select.vue";
import FormInput from "@/views/pages/uiinterface/forms/form-elements/forminput/form-input.vue";
import Formmask from "@/views/pages/uiinterface/forms/form-elements/formmask/formmask.vue";
import Formfileupload from "@/views/pages/uiinterface/forms/form-elements/formfile-upload.vue";
import FormHorizontal from "@/views/pages/uiinterface/forms/layouts/form-horizontal.vue";
import Formvertical from "@/views/pages/uiinterface/forms/layouts/form-vertical.vue";
import Formfloatinglabels from "@/views/pages/uiinterface/forms/layouts/form-floating-labels.vue";
import Formvalidation from "@/views/pages/uiinterface/forms/form-validation.vue";
import Formselect2 from "@/views/pages/uiinterface/forms/form-select2.vue";
import FormElements from "@/views/pages/uiinterface/forms/form-elements/form-elements.vue";
import Formwizard from "@/views/pages/uiinterface/forms/form-wizard.vue";
import Advancedui from "@/views/pages/uiinterface/advancedUI/advanced-ui.vue";
import UI_Ribbon from "@/views/pages/uiinterface/advancedUI/ui-ribbon.vue";
import UI_Clipboard from "@/views/pages/uiinterface/advancedUI/ui-clipboard.vue";
import UI_Drag_Drop from "@/views/pages/uiinterface/advancedUI/ui-drag-drop.vue";
import UI_Text_Editor from "@/views/pages/uiinterface/advancedUI/ui-text-editor.vue";
import UI_Counter from "@/views/pages/uiinterface/advancedUI/ui-counter.vue";
import UI_Scrollbar from "@/views/pages/uiinterface/advancedUI/ui-scrollbar.vue";
import UI_Rating from "@/views/pages/uiinterface/advancedUI/ui-rating.vue";
import UI_Stickynote from "@/views/pages/uiinterface/advancedUI/ui-stickynote.vue";
import UI_Timeline from "@/views/pages/uiinterface/advancedUI/ui-timeline.vue";
import BaseUi from "@/views/pages/uiinterface/baseui/base-ui.vue";
import UI_Alerts from "@/views/pages/uiinterface/baseui/ui-alerts.vue";
import UI_Accordion from "@/views/pages/uiinterface/baseui/ui-accordion.vue";
import UI_Avatar from "@/views/pages/uiinterface/baseui/ui-avatar.vue";
import UI_Badges from "@/views/pages/uiinterface/baseui/ui-badges.vue";
import UI_Borders from "@/views/pages/uiinterface/baseui/ui-borders.vue";
import UI_Buttons from "@/views/pages/uiinterface/baseui/ui-buttons.vue";
import UI_Buttons_group from "@/views/pages/uiinterface/baseui/ui-buttons-group.vue";
import UI_Breadcrumb from "@/views/pages/uiinterface/baseui/ui-breadcrumb.vue";
import UI_Cards from "@/views/pages/uiinterface/baseui/ui-cards.vue";
import UI_Carousel from "@/views/pages/uiinterface/baseui/carousel/ui-carousel.vue";
import UI_Colors from "@/views/pages/uiinterface/baseui/ui-colors.vue";
import UI_Dropdowns from "@/views/pages/uiinterface/baseui/ui-dropdowns.vue";
import UI_Grid from "@/views/pages/uiinterface/baseui/ui-grid.vue";
import UI_Images from "@/views/pages/uiinterface/baseui/ui-images.vue";
import UI_Lightbox from "@/views/pages/uiinterface/baseui/lightbox/ui-lightbox.vue";
import UI_Media from "@/views/pages/uiinterface/baseui/ui-media.vue";
import UI_Modals from "@/views/pages/uiinterface/baseui/ui-modals.vue";
import UI_Offcanvas from "@/views/pages/uiinterface/baseui/ui-offcanvas.vue";
import UI_Pagination from "@/views/pages/uiinterface/baseui/ui-pagination.vue";
import UI_Popovers from "@/views/pages/uiinterface/baseui/ui-popovers.vue";
import UI_Progress from "@/views/pages/uiinterface/baseui/ui-progress.vue";
import UI_Placeholders from "@/views/pages/uiinterface/baseui/ui-placeholders.vue";
import UI_Rangeslider from "@/views/pages/uiinterface/advancedUI/ui-rangeslider.vue";
import Ui_Navtabs from "@/views/pages/uiinterface/baseui/ui-nav-tabs.vue";
import UI_Spinner from "@/views/pages/uiinterface/baseui/ui-spinner.vue";
import UI_Sweetalerts from "@/views/pages/uiinterface/baseui/ui-sweetalerts.vue";
import UI_Tooltips from "@/views/pages/uiinterface/baseui/ui-tooltips.vue";
import UI_Typography from "@/views/pages/uiinterface/baseui/ui-typography.vue";
import UI_Video from "@/views/pages/uiinterface/baseui/ui-video.vue";
import Icons from "@/views/pages/uiinterface/icons/ui-icons.vue";
import UI_Iconfontawesome from "@/views/pages/uiinterface/icons/icon-fontawesome.vue";
import UI_Iconfeather from "@/views/pages/uiinterface/icons/icon-feather.vue";
import UI_Iconionic from "@/views/pages/uiinterface/icons/icon-ionic.vue";
import UI_Iconmaterial from "@/views/pages/uiinterface/icons/icon-material.vue";
import UI_Iconpe7 from "@/views/pages/uiinterface/icons/icon-pe7.vue";
import UI_Iconsimpleline from "@/views/pages/uiinterface/icons/icon-simpleline.vue";
import UI_Iconthemify from "@/views/pages/uiinterface/icons/icon-themify.vue";
import UI_Iconweather from "@/views/pages/uiinterface/icons/icon-weather.vue";
import UI_Icontypicon from "@/views/pages/uiinterface/icons/icon-typicon.vue";
import UI_Iconflag from "@/views/pages/uiinterface/icons/icon-flag.vue";

// Authentication component
import marketingLanding from "@/views/public/marketing-landing.vue";
import WorkspaceNotFound from "@/views/public/workspace-not-found.vue";
import LoginIndex from "@/views/pages/authentication/login-index.vue";
import LoginTwo from "@/views/pages/authentication/login-two.vue";
import LoginThree from "@/views/pages/authentication/login-three.vue";
import RegisterIndex from "@/views/pages/authentication/register-index.vue";
import VerifyEmailNotice from "@/views/pages/authentication/verify-email-notice.vue";
import selectPlan from "@/views/pages/pages/plan/select-plan.vue";
import RegisterTwo from "@/views/pages/authentication/register-two.vue";
import RegisterThree from "@/views/pages/authentication/register-three.vue";
import ForgotPassword from "@/views/pages/authentication/forgot-password.vue";
import ForgotPasswordTwo from "@/views/pages/authentication/forgot-password-two.vue";
import ForgotPasswordThree from "@/views/pages/authentication/forgot-password-three.vue";
import ResetPassword from "@/views/pages/authentication/reset-password.vue";
import ResetPasswordTwo from "@/views/pages/authentication/reset-password-two.vue";
import ResetPasswordThree from "@/views/pages/authentication/reset-password-three.vue";
import EmailVerification from "@/views/pages/authentication/email-verification.vue";
import EmailVerificationTwo from "@/views/pages/authentication/email-verification-two.vue";
import EmailVerificationThree from "@/views/pages/authentication/email-verification-three.vue";
import TwoStepVerification from "@/views/pages/authentication/two-step-verification.vue";
import TwoStepVerificationTwo from "@/views/pages/authentication/two-step-verification-two.vue";
import TwoStepVerificationThree from "@/views/pages/authentication/two-step-verification-three.vue";
import LockScreen from "@/views/pages/authentication/lock-screen.vue";
import Error404 from "@/views/pages/authentication/error-404.vue";
import Error500 from "@/views/pages/authentication/error-500.vue";
import BillingPage from "@/views/pages/billing/billing-page.vue";
import CompanySuspended from "@/views/pages/errors/company-suspended.vue";
import RenewalRequired from "@/views/pages/errors/renewal-required.vue";

import FormLayouts from "@/views/pages/uiinterface/forms/layouts/form-layouts.vue";

import packagesList from "@/views/pages/superadmin/packages/packages-list.vue";
import packagesGrid from "@/views/pages/superadmin/packages/packages-grid.vue";
import domainList from "@/views/pages/superadmin/domain/domain-list.vue";
import tenantsList from "@/views/pages/superadmin/tenants/tenants-list.vue";
import purchaseTransaction from "@/views/pages/superadmin/purchase/purchase-transaction.vue";
import demoRequestsList from "@/views/pages/superadmin/demo-requests/demo-requests-list.vue";
import TasksIndex from "@/views/pages/projects/task/tasks-index.vue";
import TaskBoard from "@/views/pages/projects/task/task-board.vue";
import ContactsGrid from "@/views/pages/crm/contacts/contacts-grid.vue";
import ContactsList from "@/views/pages/crm/contacts/contacts-list.vue";
import ContactDetails from "@/views/pages/crm/contacts/contact-details.vue";
import CrmCompaniesGrid from "@/views/pages/crm/companies/companies-grid.vue";
import companiesGrid from "@/views/pages/superadmin/companies/companies-grid.vue";
import pipelineList from "@/views/pages/crm/pipeline/pipeline-list.vue";
import AnalyticsList from "@/views/pages/crm/analytics/analytics-list.vue";
import ActivityList from "@/views/pages/crm/activity/activity-list.vue";
import HrmIndex from "@/views/pages/hrm/hrm-index.vue";
import HolidaysList from "@/views/pages/hrm/holidays/holidays-list.vue";
import LeavesAdmin from "@/views/pages/hrm/attendance/leaves/leaves-admin.vue";
import LeaveIndex from "@/views/pages/hrm/attendance/leaves/leave-index.vue";
import LeavesEmployee from "@/views/pages/hrm/attendance/leaves/leaves-employee.vue";
import LeaveSettings from "@/views/pages/hrm/attendance/leaves/leave-settings.vue";
import AttendanceIndex from "@/views/pages/hrm/attendance/attendance-index.vue";
import AttendanceAdmin from "@/views/pages/hrm/attendance/attendance-admin.vue";
import AttendanceEmployee from "@/views/pages/hrm/attendance/attendance-employee.vue";
import TimesheetsList from "@/views/pages/hrm/attendance/timesheets-list.vue";
import MyTimesheetsList from "@/views/pages/hrm/attendance/my-timesheets-list.vue";
import MySchedule from "@/views/pages/hrm/attendance/my-schedule.vue";
import ScheduleTiming from "@/views/pages/hrm/attendance/schedule-timing.vue";
import PerformanceIndex from "@/views/pages/hrm/attendance/performance/performance-index.vue";
import PerformanceIndicator from "@/views/pages/hrm/attendance/performance/performance-indicator.vue";
import PerformanceReview from "@/views/pages/hrm/attendance/performance/performance-review.vue";
import PerformanceReviewList from "@/views/pages/hrm/attendance/performance/performance-review-list.vue";
import PerformanceAppraisal from "@/views/pages/hrm/attendance/performance/performance-appraisal.vue";
import GoalTracking from "@/views/pages/hrm/attendance/performance/goal-tracking.vue";
import GoalType from "@/views/pages/hrm/attendance/performance/goal-type.vue";
import TrainingIndex from "@/views/pages/hrm/attendance/training/training-index.vue";
import TrainingList from "@/views/pages/hrm/attendance/training/training-list.vue";
import TrainersList from "@/views/pages/hrm/attendance/training/trainers-list.vue";
import TrainingType from "@/views/pages/hrm/attendance/training/training-type.vue";
import PromotionList from "@/views/pages/hrm/promotion/promotion-list.vue";
import ResignationList from "@/views/pages/hrm/resignation/resignation-list.vue";
import TerminationList from "@/views/pages/hrm/termination/termination-list.vue";
import RecuritmentIndex from "@/views/pages/recruitment/recuritment-index.vue";
import JobGrid from "@/views/pages/recruitment/jobs/job-grid.vue";
import JobList from "@/views/pages/recruitment/jobs/job-list.vue";
import CandidatesGrid from "@/views/pages/recruitment/candidates/candidates-grid.vue";
import CandidatesList from "@/views/pages/recruitment/candidates/candidates-list.vue";
import CandidatesKanban from "@/views/pages/recruitment/candidates/candidates-kanban.vue";
import RefferalsList from "@/views/pages/recruitment/refferals/refferals-list.vue";
import SalesIndex from "@/views/pages/finance-accounts/sales/sales-index.vue";
import EstimatesList from "@/views/pages/finance-accounts/sales/estimates-list.vue";
import InvoicesList from "@/views/pages/finance-accounts/sales/invoices-list.vue";
import AddInvoices from "@/views/pages/finance-accounts/sales/add-invoices.vue";
import EditInvoices from "@/views/pages/finance-accounts/sales/edit-invoices.vue";
import InvoiceDetails from "@/views/pages/finance-accounts/sales/invoice-details.vue";
import PaymentsList from "@/views/pages/finance-accounts/sales/payments-list.vue";
import ExpensesList from "@/views/pages/finance-accounts/sales/expenses-list.vue";
import ProvidentFund from "@/views/pages/finance-accounts/sales/provident-fund.vue";
import TaxesList from "@/views/pages/finance-accounts/sales/taxes-list.vue";
import CategoriesList from "@/views/pages/finance-accounts/accounting/categories-list.vue";
import ContentIndex from "@/views/pages/content/content-index.vue";
import PagesList from "@/views/pages/content/pages-list.vue";
import BlogsIndex from "@/views/pages/content/blogs/blogs-index.vue";
import BlogsGrid from "@/views/pages/content/blogs/blogs-grid.vue";
import BlogCategories from "@/views/pages/content/blogs/blog-categories.vue";
import BlogComments from "@/views/pages/content/blogs/blog-comments.vue";
import BlogTags from "@/views/pages/content/blogs/blog-tags.vue";
import LocationIndex from "@/views/pages/content/location/location-index.vue";
import CountriesList from "@/views/pages/content/location/countries-list.vue";
import StatesList from "@/views/pages/content/location/states-list.vue";
import CitiesList from "@/views/pages/content/location/cities-list.vue";
import TestimonialsList from "@/views/pages/content/testimonials-list.vue";
import FaqList from "@/views/pages/content/faq-list.vue";
import loansSavingsEntry from "@/views/pages/finance-accounts/payroll/loans-savings-entry.vue";
import loansManagement from "@/views/pages/hrm/loans/loans.vue";
import savingsManagement from "@/views/pages/hrm/savings/savings.vue";
import ReportsIndex from "@/views/pages/administration/reports/reports-index.vue";
import ExpensesReport from "@/views/pages/administration/reports/expenses-report.vue";
import InvoiceReport from "@/views/pages/administration/reports/invoice-report.vue";
import PaymentReport from "@/views/pages/administration/reports/payment-report.vue";
import ProjectReport from "@/views/pages/administration/reports/project-report.vue";
import TaskReport from "@/views/pages/administration/reports/task-report.vue";
import UserReport from "@/views/pages/administration/reports/user-report.vue";
import EmployeeReport from "@/views/pages/administration/reports/employee-report.vue";
import PayslipReport from "@/views/pages/administration/reports/payslip-report.vue";
import p9Report from "@/views/pages/administration/reports/p9-report.vue";
import legacyP9Report from "@/views/pages/administration/reports/legacy-p9-report.vue";
import p10Report from "@/views/pages/administration/reports/p10-report.vue";
import monthlyComplianceReport from "@/views/pages/administration/reports/monthly-compliance-report.vue";
import importedPayrollHistoryReport from "@/views/pages/administration/reports/imported-payroll-history-report.vue";
import bankTransfer from "@/views/pages/administration/reports/bank-transfer-report.vue";
import cashPayrollReport from "@/views/pages/administration/reports/cash-payroll-report.vue";
import cashPayrollAuditReport from "@/views/pages/administration/reports/cash-payroll-audit-report.vue";
import checkPayrollReport from "@/views/pages/administration/reports/check-payroll-report.vue";
import checkPayrollAuditReport from "@/views/pages/administration/reports/check-payroll-audit-report.vue";
import statutoryDeduction from "@/views/pages/administration/reports/statutory-report.vue";
import payrollControlTotals from "@/views/pages/administration/reports/control-total-report.vue";
import loansSavingsReport from "@/views/pages/administration/reports/loans-savings-report.vue";
import memberSavingsReport from "@/views/pages/administration/reports/member-savings-report.vue";
import loanDisbursementsReport from "@/views/pages/administration/reports/loan-disbursements-report.vue";
import loanRepaymentReport from "@/views/pages/administration/reports/loan-repayments-report.vue";
import interestEarnedReport from "@/views/pages/administration/reports/interest-earned-report.vue";
import monthlyCollectionsReport from "@/views/pages/administration/reports/monthly-collections-report.vue";
import auditTrailReport from "@/views/pages/administration/reports/audit-trail-report.vue";
import payrollComparisonReport from "@/views/pages/administration/reports/payroll-comparison-report.vue";
import musterRoll from "@/views/pages/administration/reports/muster-roll-report.vue";
import AttendanceReport from "@/views/pages/administration/reports/attendance-report.vue";
import LeaveReport from "@/views/pages/administration/reports/leave-report.vue";
import DailyReport from "@/views/pages/administration/reports/daily-report.vue";
import component from "vue3-tags-input";

const routes = [
  {
    path: "/",
    name: "public-pricing",
    component: marketingLanding,
    meta: { guestOnly: true }, // no auth required
  },
  {
    path: "/workspace-not-found",
    name: "workspace-not-found",
    component: WorkspaceNotFound,
    meta: { guestOnly: true },
  },

  {
    path: "/login",
    name: "login-index",
    component: LoginIndex,
    meta: { guestOnly: true },
  },
  {
    path: "/login-2",
    name: "login-two",
    component: LoginTwo,
    meta: { guestOnly: true },
  },
  {
    path: "/login-3",
    name: "login-three",
    component: LoginThree,
    meta: { guestOnly: true },
  },
  {
    path: "/register",
    name: "register-index",
    component: RegisterIndex,
    meta: { guestOnly: true },
  },
  {
    path: "/auth/verify-email",
    name: "verify-email-notice",
    component: VerifyEmailNotice,
    meta: { guestOnly: false },
  },

  {
    path: "/select-plan",
    name: "select-plan",
    component: selectPlan,
    meta: { requiresAuth: true },
  },
  {
    path: "/register-2",
    name: "register-two",
    component: RegisterTwo,
    meta: { guestOnly: true },
  },
  {
    path: "/register-3",
    name: "register-three",
    component: RegisterThree,
    meta: { guestOnly: true },
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: ForgotPassword,
    meta: { guestOnly: true },
  },
  {
    path: "/forgot-password-2",
    name: "forgot-password-two",
    component: ForgotPasswordTwo,
    meta: { guestOnly: true },
  },
  {
    path: "/forgot-password-3",
    name: "forgot-password-three",
    component: ForgotPasswordThree,
    meta: { guestOnly: true },
  },
  {
    path: "/reset-password",
    alias: ["/login/reset-password", "/set-password"],
    name: "reset-password",
    component: ResetPassword,
    meta: { guestOnly: true },
  },
  {
    path: "/reset-password-2",
    name: "reset-password-two",
    component: ResetPasswordTwo,
    meta: { guestOnly: true },
  },
  {
    path: "/reset-password-3",
    name: "reset-password-three",
    component: ResetPasswordThree,
    meta: { guestOnly: true },
  },
  {
    path: "/email-verification",
    name: "email-verification",
    component: EmailVerification,
    meta: { guestOnly: true },
  },
  {
    path: "/email-verification-2",
    name: "email-verification-two",
    component: EmailVerificationTwo,
    meta: { guestOnly: true },
  },
  {
    path: "/email-verification-3",
    name: "email-verification-three",
    component: EmailVerificationThree,
    meta: { guestOnly: true },
  },
  {
    path: "/two-step-verification",
    name: "two-step-verification",
    component: TwoStepVerification,
    meta: { guestOnly: true },
  },
  {
    path: "/two-step-verification-2",
    name: "two-step-verification-two",
    component: TwoStepVerificationTwo,
    meta: { guestOnly: true },
  },
  {
    path: "/two-step-verification-3",
    name: "two-step-verification-three",
    component: TwoStepVerificationThree,
    meta: { guestOnly: true },
  },
  {
    path: "/lock-screen",
    name: "lock-screen",
    component: LockScreen,
    meta: { requiresAuth: true },
  },
  {
    path: "/error-404",
    name: "error-404",
    component: Error404,
  },
  {
    path: "/error-500",
    name: "error-500",
    component: Error500,
  },
  {
    path: "/billing",
    name: "billing-page",
    component: BillingPage,
    meta: { requiresAuth: true, adminOnly: true },
  },
  {
    path: "/company-suspended",
    name: "company-suspended",
    component: CompanySuspended,
    meta: { requiresAuth: true },
  },
  {
    path: "/renewal-required",
    name: "renewal-required",
    component: RenewalRequired,
    meta: { requiresAuth: true },
  },

  {
    path: "/website-settings",
    component: websiteSettings,
    meta: { requiresAuth: true, modules: ["admin_settings"] },
    children: [
      {
        path: "",
        redirect: "/website-settings/bussiness-settings",
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "bussiness-settings",
        component: bussinessSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "seo-settings",
        component: seoSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "localization-settings",
        component: localizationSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "prefixes",
        component: prefixes,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "preferences",
        component: preferences,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "appearance",
        component: appearance,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "language",
        component: language,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "add-language",
        component: addLanguage,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "language-web",
        component: languageWeb,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "authentication-settings",
        component: authenticationSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "ai-settings",
        component: aiSettings,
        meta: { modules: ["admin_settings"] },
      },
    ],
  },
  {
    path: "/others-settings",
    component: othersSettings,
    meta: { requiresAuth: true, modules: ["admin_settings"] },
    children: [
      {
        path: "",
        redirect: "/others-settings/custom-css",
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "custom-css",
        component: customCss,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "custom-js",
        component: customJs,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "cronjob",
        component: cronJob,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "cronjob-schedule",
        component: cronjobSchedule,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "storage-settings",
        component: storageSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "ban-ip-address",
        component: banIpAddress,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "backup",
        component: backupSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "clear-cache",
        component: clearCache,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "migration-center",
        redirect: "/super-admin/migration-center",
        meta: { superAdminOnly: true },
      },
    ],
  },
  {
    path: "/financial-settings",
    component: financialSettings,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/financial-settings/payment-gateways" },
      { path: "payment-gateways", component: paymentGateway },
      { path: "tax-rates", component: taxRates },
      { path: "kenya-tax-compliance", component: kenyaTaxCompliance },
      { path: "currencies", component: currenciesSettings },
    ],
  },
  {
    path: "/system-settings",
    component: systemSettings,
    meta: { requiresAuth: true, modules: ["admin_settings"] },
    children: [
      {
        path: "",
        redirect: "/system-settings/email-settings",
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "otp-settings",
        component: otpSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "email-settings",
        component: emailSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "email-template",
        component: emailTemplate,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "sms-settings",
        component: smsSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "sms-template",
        component: smsTemplate,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "gdpr",
        component: gdprSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "maintenance-mode",
        component: maintenanceMode,
        meta: { modules: ["admin_settings"] },
      },
    ],
  },
  {
    path: "/app-settings",
    component: appSettings,
    meta: { requiresAuth: true, modules: ["admin_settings"] },
    children: [
      {
        path: "",
        redirect: "/app-settings/salary-settings",
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "salary-settings",
        component: salarySettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "leave-type",
        redirect: "/leave/leave-settings",
      },
      {
        path: "approval-settings",
        component: approvalSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "invoice-settings",
        component: invoiceSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "custom-fields",
        component: customFields,
        meta: { modules: ["admin_settings"] },
      },
    ],
  },
  {
    path: "/general-settings",
    component: generalSettings,
    meta: { requiresAuth: true, modules: ["admin_settings"] },
    children: [
      {
        path: "",
        redirect: "/general-settings/profile-settings",
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "profile-settings",
        component: profileSettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "security-settings",
        component: securitySettings,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "notification-settings",
        redirect: "/general-settings/profile-settings",
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "connected-apps",
        redirect: "/general-settings/profile-settings",
        meta: { modules: ["admin_settings"] },
      },
    ],
  },
  {
    path: "/user-management",
    component: userManagement,
    meta: { requiresAuth: true, modules: ["admin_settings"] },
    children: [
      {
        path: "",
        redirect: "/user-management/users",
        // meta: { modules: ["admin_settings"] },
      },
      {
        path: "users",
        component: userList,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "roles-permissions",
        component: rolesPermissions,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "roles-permissions/:roleId/permissions",
        component: rolePermissionsEditor,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "permission",
        component: permissionIndex,
        meta: { modules: ["admin_settings"] },
      },
    ],
  },
  // {
  //   path: "/supports",
  //   component: helpSupports,
  //   meta: { modules: ["admin_help_support"] },
  //   children: [
  //     { path: "", redirect: "/supports/knowledgebase" },
  //     { path: "knowledgebase", component: knowledgebaseIndex },
  //     { path: "knowledgebase-view", component: knowledgebaseView },
  //     { path: "knowledgebase-details", component: knowledgebaseDetails },
  //   ],
  // },
  {
    path: "/asset",
    component: assetsIndex,
    meta: { requiresAuth: true, modules: ["admin_assets"] },
    children: [
      {
        path: "",
        redirect: "/asset/assets",
        // meta: { modules: ["admin_assets"] },
      },
      {
        path: "assets",
        component: assetsList,
        meta: { modules: ["admin_assets"] },
      },
      {
        path: "asset-categories",
        component: assetsCategories,
        meta: { modules: ["admin_assets"] },
      },
    ],
  },
  {
    path: "/layouts",
    component: layoutIndex,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/layouts/layout-horizontal" },
      { path: "layout-horizontal", component: layoutHorizontal },
      { path: "layout-detached", component: layoutDetached },
      { path: "layout-modern", component: layoutModern },
      { path: "layout-two-column", component: layoutTwocolum },
      { path: "layout-hovered", component: layoutHovered },
      { path: "layout-box", component: layoutBox },
      { path: "layout-horizontal-single", component: layoutHorizontalSingle },
      { path: "layout-horizontal-overlay", component: layoutHorizontalOverlay },
      { path: "layout-horizontal-box", component: layoutHorizontalBox },
      {
        path: "layout-horizontal-sidemenu",
        component: layoutHorizontalSidemenu,
      },
      {
        path: "layout-vertical-transparent",
        component: layoutVerticalTransparent,
      },
      { path: "layout-without-header", component: layoutWithoutHeader },
      { path: "layout-rtl", component: layoutRtl },
      { path: "layout-dark", component: layoutDark },
    ],
  },
  {
    path: "/terms-condition",
    component: termsCondition,
  },
  {
    path: "/privacy-policy",
    component: privacyPolicy,
  },
  {
    path: "/pages",
    component: pagesIndex,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/pages/starter" },
      { path: "starter", component: starterIndex },
      { path: "profile", component: profileIndex },
      { path: "company-profile", component: companyProfileIndex },
      { path: "gallery", component: galleryIndex },
      { path: "search-result", component: searchResult },
      { path: "timeline", component: timelineIndex },
      { path: "pricing", component: pricingIndex },
      { path: "coming-soon", component: comingSoon },
      { path: "under-maintenance", component: underMaintenance },
      { path: "under-construction", component: underConstruction },
      { path: "api-keys", component: apiKeys },
      { path: "terms-condition", component: termsCondition },
      { path: "privacy-policy", component: privacyPolicy },
    ],
  },
  {
    path: "/payroll",
    component: payrollIndex,
    meta: { requiresAuth: true, modules: ["hrm_payroll"] },
    children: [
      {
        path: "",
        redirect: "/payroll/payroll-settings",
        // meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "employee-salary",
        component: employeeSalary,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "payslip",
        redirect: "/reports/payslip-report",
      },

      {
        path: "my-payslips",
        component: mypayslipIndex,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "my-p9",
        component: myp9Index,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "payroll",
        component: payrollAdditions,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "payroll-overtime",
        component: payrollOvertime,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "payroll-overtime-entries",
        component: payrollOvertimeEntries,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "payroll-deduction",
        component: payrollDeduction,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "period-adjustments",
        component: payrollPeriodAdjustments,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "payroll-settings",
        component: payrollSettings,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "bank-transfer-formats",
        component: payrollBankTransferTemplates,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "payroll-meta-conversion",
        component: payrollMetaConversion,
        meta: { modules: ["hrm_payroll"] },
      },
      {
        path: "payroll-presets",
        component: adminPayrollPresets,
        meta: { modules: ["hrm_payroll"], superAdminOnly: true },
      },
    ],
  },
  {
    path: "/accounting",
    component: accountingIndex,
    meta: { requiresAuth: true, modules: ["fa_accounting"] },
    children: [
      {
        path: "",
        redirect: "/accounting/budgets",
        // meta: { modules: ["fa_accounting"] },
      },
      {
        path: "budgets",
        component: budgetsIndex,
        meta: { modules: ["fa_accounting"] },
      },
      {
        path: "budget-expenses",
        component: budgetsExpenses,
        meta: { modules: ["fa_accounting"] },
      },
      {
        path: "budget-revenues",
        component: budgetsRevenues,
        meta: { modules: ["fa_accounting"] },
      },
      {
        path: "categories",
        component: CategoriesList,
        meta: { modules: ["fa_accounting"] },
      },
    ],
  },
  {
    path: "/content",
    component: ContentIndex,
    meta: { requiresAuth: true, modules: ["admin_settings"] },
    children: [
      {
        path: "",
        redirect: "/content/pages",
        // meta: { modules: ["admin_settings"] },
      },
      {
        path: "pages",
        component: PagesList,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "testimonials",
        component: TestimonialsList,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "faq",
        component: FaqList,
        meta: { modules: ["admin_settings"] },
      },
    ],
  },
  {
    path: "/notifications",
    component: notificationsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports",
    component: ReportsIndex,
    meta: { requiresAuth: true, modules: ["admin_reports"] },
    children: [
      { path: "", redirect: "/reports/monthly-paye-report" },
      {
        path: "monthly-paye-report",
        component: monthlyComplianceReport,
        meta: {
          modules: ["admin_reports"],
          complianceType: "paye",
          reportTitle: "PAYE (P10) Monthly Report",
          reportGroup: "Monthly Reports",
        },
      },
      {
        path: "monthly-nssf-report",
        component: monthlyComplianceReport,
        meta: {
          modules: ["admin_reports"],
          complianceType: "nssf",
          reportTitle: "NSSF Monthly Report",
          reportGroup: "Monthly Reports",
        },
      },
      {
        path: "monthly-shif-report",
        component: monthlyComplianceReport,
        meta: {
          modules: ["admin_reports"],
          complianceType: "shif",
          reportTitle: "SHIF / SHA Monthly Report",
          reportGroup: "Monthly Reports",
        },
      },
      {
        path: "monthly-ahl-report",
        component: monthlyComplianceReport,
        meta: {
          modules: ["admin_reports"],
          complianceType: "ahl",
          reportTitle: "Affordable Housing Levy Monthly Report",
          reportGroup: "Monthly Reports",
        },
      },
      {
        path: "expenses-report",
        component: ExpensesReport,
        meta: { modules: ["fa_sales"] },
      },
      {
        path: "invoice-report",
        component: InvoiceReport,
        meta: { modules: ["fa_sales"] },
      },
      {
        path: "payment-report",
        component: PaymentReport,
        meta: { modules: ["fa_sales"] },
      },
      {
        path: "project-report",
        component: ProjectReport,
        meta: { modules: ["projects"] },
      },
      {
        path: "task-report",
        component: TaskReport,
        meta: { modules: ["tasks"] },
      },
      {
        path: "user-report",
        component: UserReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "employee-report",
        component: EmployeeReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "payslip-report",
        component: PayslipReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "bank-transfer-report",
        component: bankTransfer,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "cash-payroll-report",
        component: cashPayrollReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "cash-payroll-audit-report",
        component: cashPayrollAuditReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "check-payroll-report",
        component: checkPayrollReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "check-payroll-audit-report",
        component: checkPayrollAuditReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "statutory-deduction-report",
        component: statutoryDeduction,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "payroll-control-totals",
        component: payrollControlTotals,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "loans-savings-report",
        component: loansSavingsReport,
        meta: { modules: ["admin_reports", "loans", "savings"] },
      },
      {
        path: "member-savings-report",
        component: memberSavingsReport,
        meta: { modules: ["admin_reports", "savings"] },
      },
      {
        path: "loan-disbursement-report",
        component: loanDisbursementsReport,
        meta: { modules: ["admin_reports", "loans"] },
      },
      {
        path: "loan-repayment-report",
        component: loanRepaymentReport,
        meta: { modules: ["admin_reports", "loans"] },
      },
      {
        path: "interest-earned-report",
        component: interestEarnedReport,
        meta: { modules: ["admin_reports", "loans"] },
      },
      {
        path: "monthly-collection-report",
        component: monthlyCollectionsReport,
        meta: { modules: ["admin_reports", "loans", "savings"] },
      },
     
      {
        path: "audit-log-report",
        component: auditTrailReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "comparative-report",
        component: payrollComparisonReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "muster-roll-report",
        component: musterRoll,
        meta: { modules: ["admin_reports"] },
      },
        {
          path: "p9-report",
          component: p9Report,
          meta: { modules: ["admin_reports"] },
        },
        {
          path: "legacy-p9-report",
          component: legacyP9Report,
          meta: { modules: ["admin_reports"] },
        },
        {
          path: "p10-report",
          component: p10Report,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "attendance-report",
        component: AttendanceReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "leave-report",
        component: LeaveReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "daily-report",
        component: DailyReport,
        meta: { modules: ["admin_reports"] },
      },
      {
        path: "imported-payroll-history-report",
        component: importedPayrollHistoryReport,
        meta: { modules: ["admin_reports"] },
      },
    ],
  },
  {
    path: "/location",
    component: LocationIndex,
    meta: { requiresAuth: true, modules: ["admin_settings"] },
    children: [
      {
        path: "",
        redirect: "/location/countries",
        // meta: { modules: ["admin_settings"] },
      },
      {
        path: "countries",
        component: CountriesList,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "states",
        component: StatesList,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "cities",
        component: CitiesList,
        meta: { modules: ["admin_settings"] },
      },
    ],
  },
  {
    path: "/blog",
    component: BlogsIndex,
    meta: { requiresAuth: true, modules: ["admin_settings"] },
    children: [
      {
        path: "",
        redirect: "/blog/blogs",
        // meta: { modules: ["admin_settings"] },
      },
      {
        path: "blogs",
        component: BlogsGrid,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "blog-categories",
        component: BlogCategories,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "blog-comments",
        component: BlogComments,
        meta: { modules: ["admin_settings"] },
      },
      {
        path: "blog-tags",
        component: BlogTags,
        meta: { modules: ["admin_settings"] },
      },
    ],
  },
  {
    path: "/sales",
    component: SalesIndex,
    meta: { requiresAuth: true, modules: ["fa_sales"] },
    children: [
      {
        path: "",
        redirect: "/sales/estimates",
        // meta: { modules: ["fa_sales"] },
      },

      // Estimates (existing)
      {
        path: "estimates",
        component: EstimatesList,
        meta: { modules: ["fa_sales"] },
      },

      // 🔹 Invoices
      {
        path: "invoices",
        name: "sales.invoices.list",
        component: InvoicesList,
        meta: { modules: ["fa_sales"] },
      },
      {
        path: "invoices/add-invoices",
        name: "sales.invoices.create",
        component: AddInvoices,
        meta: { modules: ["fa_sales"] },
      },
      {
        path: "invoices/:id/edit",
        name: "sales.invoices.edit",
        component: EditInvoices,
        props: true, // so you can receive `props: { id }` or use useRoute()
        meta: { modules: ["fa_sales"] },
      },
      {
        path: "invoices/:id",
        name: "sales.invoices.show",
        component: InvoiceDetails,
        props: true,
        meta: { modules: ["fa_sales"] },
      },

      // Payments / Expenses / Others
      {
        path: "payments",
        component: PaymentsList,
        meta: { modules: ["fa_sales"] },
      },
      {
        path: "expenses",
        component: ExpensesList,
        meta: { modules: ["fa_sales"] },
      },
      {
        path: "provident-fund",
        component: ProvidentFund,
        meta: { modules: ["fa_sales"] },
      },
      { path: "taxes", component: TaxesList, meta: { modules: ["fa_sales"] } },
    ],
  },

  // {
  //   path: "/recuritment",
  //   component: RecuritmentIndex,
  //   meta: {
  //     requiresAuth: true,
  //     modules: ["recruit_jobs", "recruit_candidates", "recruit_referrals"],
  //   },
  //   children: [
  //     { path: "", redirect: "/recuritment/job-grid" },
  //     { path: "job-grid", component: JobGrid },
  //     { path: "job-list", component: JobList },
  //     { path: "candidates-grid", component: CandidatesGrid },
  //     { path: "candidates", component: CandidatesList },
  //     { path: "candidates-kanban", component: CandidatesKanban },
  //     { path: "refferals", component: RefferalsList },
  //   ],
  // },
  {
    path: "/hrm",
    component: HrmIndex,
    meta: {
      requiresAuth: true,
      modules: [
        "hrm_holidays",
        "hrm_promotion",
        "hrm_resignation",
        "hrm_termination",
      ],
    },
    children: [
      {
        path: "",
        redirect: "/hrm/holidays",
        // meta: { modules: ["hrm_holidays"] },
      },
      {
        path: "holidays",
        component: HolidaysList,
        meta: { modules: ["hrm_holidays"] },
      },
      {
        path: "promotion",
        component: PromotionList,
        meta: { modules: ["hrm_promotion"] },
      },
      {
        path: "resignation",
        component: ResignationList,
        meta: { modules: ["hrm_resignation"] },
      },
      {
        path: "termination",
        component: TerminationList,
        meta: { modules: ["hrm_termination"] },
      },
    ],
  },

  {
    path: "/training",
    component: TrainingIndex,
    meta: { requiresAuth: true, modules: ["hrm_training"] },
    children: [
      {
        path: "",
        redirect: "/training/training-list",
      },
      {
        path: "training-list",
        component: TrainingList,
        meta: { modules: ["hrm_training"] },
      },
      {
        path: "trainers",
        component: TrainersList,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/training/training-list";
          } catch {
            return "/training/training-list";
          }
        },
        meta: { modules: ["hrm_training"] },
      },
      {
        path: "training-type",
        component: TrainingType,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/training/training-list";
          } catch {
            return "/training/training-list";
          }
        },
        meta: { modules: ["hrm_training"] },
      },
    ],
  },
  {
    path: "/performance",
    component: PerformanceIndex,
    meta: { requiresAuth: true, modules: ["hrm_performance"] },
    children: [
      {
        path: "",
        redirect: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? "/performance/performance-indicator"
              : "/performance/performance-review-list";
          } catch {
            return "/performance/performance-review-list";
          }
        },
      },
      {
        path: "performance-indicator",
        component: PerformanceIndicator,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/performance/performance-review-list";
          } catch {
            return "/performance/performance-review-list";
          }
        },
        meta: { modules: ["hrm_performance"] },
      },
      {
        path: "performance-review",
        component: PerformanceReview,
        meta: { modules: ["hrm_performance"] },
      },
      {
        path: "performance-review/new",
        component: PerformanceReview,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/performance/performance-review-list";
          } catch {
            return "/performance/performance-review-list";
          }
        },
        meta: { modules: ["hrm_performance"] },
      },
      {
        path: "performance-review-list",
        component: PerformanceReviewList,
        meta: { modules: ["hrm_performance"] },
      },
      {
        path: "performance-appraisal",
        component: PerformanceAppraisal,
        meta: { modules: ["hrm_performance"] },
      },
      {
        path: "goal-tracking",
        component: GoalTracking,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/performance/performance-review-list";
          } catch {
            return "/performance/performance-review-list";
          }
        },
      },
      {
        path: "goal-type",
        component: GoalType,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/performance/performance-review-list";
          } catch {
            return "/performance/performance-review-list";
          }
        },
      },
    ],
  },
  {
    path: "/attendance",
    component: AttendanceIndex,
    meta: { requiresAuth: true, modules: ["hrm_attendance"] },
    children: [
      {
        path: "",
        redirect: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? "/attendance/attendance-admin"
              : "/attendance/attendance-employee";
          } catch {
            return "/attendance/attendance-employee";
          }
        },
      },
      {
        path: "attendance-admin",
        component: AttendanceAdmin,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/attendance/attendance-employee";
          } catch {
            return "/attendance/attendance-employee";
          }
        },
        meta: { modules: ["hrm_attendance"] },
      },
      {
        path: "attendance-employee",
        component: AttendanceEmployee,
        meta: { modules: ["hrm_attendance"] },
      },
      {
        path: "timesheets",
        component: TimesheetsList,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/attendance/my-timesheets";
          } catch {
            return "/attendance/my-timesheets";
          }
        },
        meta: { modules: ["hrm_attendance"] },
      },
      {
        path: "my-timesheets",
        component: MyTimesheetsList,
        meta: { modules: ["hrm_attendance"] },
      },
      {
        path: "my-schedule",
        component: MySchedule,
        meta: { modules: ["hrm_attendance"] },
      },
      {
        path: "schedule-timing",
        component: ScheduleTiming,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/attendance/attendance-employee";
          } catch {
            return "/attendance/attendance-employee";
          }
        },
        meta: { modules: ["hrm_attendance"] },
      },
      {
        path: "overtime",
        redirect: "/payroll/payroll-overtime",
        meta: { modules: ["hrm_attendance"] },
      },
    ],
  },
  {
    path: "/leave",
    component: LeaveIndex,
    meta: { requiresAuth: true, modules: ["hrm_attendance"] },
    children: [
      {
        path: "",
        redirect: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? "/leave/leaves-admin"
              : "/leave/leaves-employee";
          } catch {
            return "/leave/leaves-employee";
          }
        },
        // meta: { modules: ["hrm_attendance"] },
      },
      {
        path: "leaves-admin",
        component: LeavesAdmin,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];
            const perms = Array.isArray(user?.permissions) ? user.permissions : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr") ||
              perms.includes("Leaves.Approve") ||
              !!user?.is_line_manager
              ? true
              : "/leave/leaves-employee";
          } catch {
            return "/leave/leaves-employee";
          }
        },
        meta: { modules: ["hrm_attendance"] },
      },
      {
        path: "leaves-employee",
        component: LeavesEmployee,
        meta: { modules: ["hrm_attendance"] },
      },
      {
        path: "leave-settings",
        component: LeaveSettings,
        beforeEnter: () => {
          try {
            const raw = localStorage.getItem("user");
            const user = raw ? JSON.parse(raw) : null;
            const roles = Array.isArray(user?.roles)
              ? user.roles.map((r) => (typeof r === "string" ? r : r?.name))
              : [];

            return roles.includes("Admin") ||
              roles.includes("HR") ||
              roles.includes("Hr")
              ? true
              : "/leave/leaves-employee";
          } catch {
            return "/leave/leaves-employee";
          }
        },
        meta: { modules: ["hrm_attendance"] },
      },
    ],
  },
  {
    path: "/tickets",
    component: ticketsIndex,
    meta: { requiresAuth: true, modules: ["hrm_tickets"] },
    children: [
      {
        path: "",
        redirect: "/tickets/ticket",
        // meta: { modules: ["hrm_tickets"] },
      },
      {
        path: "ticket",
        component: ticketList,
        meta: { modules: ["hrm_tickets"] },
      },
      {
        path: "tickets-grid",
        redirect: "/tickets/ticket",
        meta: { modules: ["hrm_tickets"] },
      },
      {
        path: "tickets-details",
        component: ticketsDetails,
        meta: { modules: ["hrm_tickets"] },
      },
    ],
  },
  {
    path: "/employee",
    component: employeesIndex,
    meta: { requiresAuth: true, modules: ["hrm_employee"] },
    children: [
      {
        path: "",
        redirect: "/employee/employee-list",
        // meta: { modules: ["hrm_employee"] },
      },
      {
        path: "employee-list",
        component: employeesList,
        meta: { modules: ["hrm_employee"] },
      },
      {
        path: "employee-grid",
        component: employeesGrid,
        meta: { modules: ["hrm_employee"] },
      },
      {
        path: "employee-details",
        component: employeeDetails,
        meta: { modules: ["hrm_employee"] },
      },
      {
        path: "departments",
        component: employeeDepartment,
        meta: { modules: ["hrm_employee"] },
      },
      {
        path: "sections",
        component: employeeSections,
        meta: { modules: ["hrm_employee"] },
      },
      {
        path: "designations",
        component: employeeDesignations,
        meta: { modules: ["hrm_employee"] },
      },
      {
        path: "org-chart",
        component: employeeOrgChart,
        meta: { modules: ["hrm_employee"] },
      },
      {
        path: "policy",
        component: employeePolicy,
        meta: { modules: ["hrm_employee"] },
      },
    ],
  },

  {
    path: "/finance/payroll/loans-savings",
    redirect: "/payroll/payroll-adjustments",
  },

  {
    path: "/payroll/payroll-adjustments",
    component: loansSavingsEntry,
    meta: { requiresAuth: true, modules: ["hrm_payroll"] },
  },

  {
    path: "/loans-management/loans",
    component: loansManagement,
    meta: { requiresAuth: true, modules: ["loans"] },
  },

  {
    path: "/savings-management/savings",
    component: savingsManagement,
    meta: { requiresAuth: true, modules: ["savings"] },
  },

  {
    path: "/crm",
    component: crmIndex,
    meta: {
      requiresAuth: true,
      modules: [
        "crm_contacts",
        "crm_clients",
        "crm_deals",
        "crm_leads",
        "crm_pipeline",
        "crm_analytics",
        "crm_activities",
      ],
    },
    children: [
      {
        path: "",
        redirect: "/crm/deals-grid",
        // meta: { modules: ["crm_deals"] },
      },
      {
        path: "deals-list",
        name: "crm.deals.list",
        component: dealsList,
        meta: { modules: ["crm_deals"] },
      },
      {
        path: "deals-grid",
        name: "crm.deals.grid",
        component: dealsGrid,
        meta: { modules: ["crm_deals"] },
      },
      {
        path: "deals-details/:id(\\d+)",
        name: "crm.deals.details",
        component: dealsDetails,
        props: true,
        meta: { modules: ["crm_deals"] },
      },
      {
        path: "leads-list",
        name: "crm.leads.list",
        component: leadsList,
        meta: { modules: ["crm_leads"] },
      },
      {
        path: "leads-grid",
        name: "crm.leads.grid",
        component: leadsGrid,
        meta: { modules: ["crm_leads"] },
      },
      {
        path: "leads-details/:id(\\d+)",
        name: "crm.leads.details",
        component: leadsDetails,
        props: true,
        meta: { modules: ["crm_leads"] },
      },

      {
        path: "contacts",
        name: "crm.contacts.list",
        component: ContactsList,
        meta: { modules: ["crm_contacts"] },
      },
      {
        path: "contacts-grid",
        name: "crm.contacts.grid",
        component: ContactsGrid,
        meta: { modules: ["crm_contacts"] },
      },
      {
        path: "contacts-details/:id(\\d+)",
        name: "crm.contacts.details",
        component: ContactDetails,
        props: true,
        meta: { modules: ["crm_contacts"] },
      },
      {
        path: "clients-grid",
        name: "crm.clients.grid",
        component: clientsGrid,
        meta: { modules: ["crm_clients"] },
      },
      {
        path: "clients",
        name: "crm.clients.list",
        component: clientsList,
        meta: { modules: ["crm_clients"] },
      },
      {
        path: "clients-details/:id(\\d+)",
        name: "crm.clients.details",
        component: clientsDetails,
        props: true,
        meta: { modules: ["crm_clients"] },
      },
      {
        path: "companies-crm",
        name: "crm.companies.list",
        component: CrmCompaniesList,
      },
      {
        path: "companies-grid",
        name: "crm.companies.grid",
        component: CrmCompaniesGrid,
      },
      {
        path: "companies-details/:id(\\d+)",
        name: "crm.companies.details",
        component: CrmCompanyDetails,
        props: true,
      },
      {
        path: "pipeline",
        component: pipelineList,
        meta: { modules: ["crm_pipeline"] },
      },
      {
        path: "analytics",
        component: AnalyticsList,
        meta: { modules: ["crm_analytics"] },
      },
      {
        path: "activity",
        component: ActivityList,
        meta: { modules: ["crm_activities"] },
      },
    ],
  },
  {
    path: "/projects",
    component: projectsIndex,
    meta: { requiresAuth: true, modules: ["projects", "tasks", "task_board"] },
    children: [
      {
        path: "",
        redirect: "/projects/projects-grid",
        // meta: { modules: ["projects"] },
      },

      {
        path: "projects-grid",
        component: projectGrid,
        meta: { modules: ["projects"] },
      },
      {
        path: "projects-list",
        component: projectList,
        meta: { modules: ["projects"] },
      },

      // 🔥 Updated dynamic details route
      {
        path: "projects-details/:id",
        component: projectDetails,
        name: "project.details",
        meta: { modules: ["projects"] },
      },

      { path: "tasks", component: TasksIndex, meta: { modules: ["tasks"] } },
      {
        path: "task-board",
        component: TaskBoard,
        meta: { modules: ["task_board"] },
      },
      {
        path: "task-details",
        redirect: "/projects/tasks",
        meta: { modules: ["tasks"] },
      },
    ],
  },

  {
    path: "/super-admin",
    component: superAdmin,
    meta: { requiresAuth: true, superAdminOnly: true },
    children: [
      { path: "", redirect: "/super-admin/dashboard" },
      { path: "dashboard", component: superDashboard },
      { path: "companies", component: companiesList },
      { path: "companies-grid", component: companiesGrid },
      { path: "companies-details/:id", component: companiesDetails },

      { path: "subscription", component: superSubscription },
      { path: "packages", component: packagesList },
      { path: "packages-grid", component: packagesGrid },
      { path: "domain", redirect: "/super-admin/tenants" },
      { path: "tenants", component: tenantsList },
      { path: "purchase-transaction", component: purchaseTransaction },
      { path: "migration-center", component: migrationCenter },
      { path: "demo-requests", component: demoRequestsList },
    ],
  },
  {
    path: "/applications",
    component: applicationIndex,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/applications/chat" },
      { path: "chat", component: chatIndex },
      { path: "events", component: calendarIndex },
      { path: "email", component: emailIndex },
      { path: "email-reply", component: emailReply },
      { path: "todo", component: todoIndex },
      { path: "todo-list", component: todoList },
      { path: "notes", component: notesIndex },
      { path: "social-feed", component: socialFeed },
      { path: "invoices", component: invoicesApp },
      { path: "add-invoices", component: addInvoices },
      { path: "edit-invoices", component: editInvoices },
      { path: "invoice-details", component: invoiceDetails },
      { path: "file-manager", component: fileManager },
      { path: "kanban-view", component: kanbanView },
    ],
  },
  {
    path: "/calls",
    component: applicationIndex,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/calls/voice-call" },
      { path: "voice-call", component: voiceCall },
      { path: "video-call", component: videoCall },
      { path: "outgoing-call", component: outgoingCall },
      { path: "incoming-call", component: incomingCall },
      { path: "call-history", component: callHistory },
    ],
  },
  {
    path: "/dashboard",
    component: dashboardIndex,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/dashboard/admin-dashboard" },
      {
        path: "admin-dashboard",
        component: adminDashboard,
        // meta: { modules: ["admin_reports"] },
      },
      {
        path: "employee-dashboard",
        component: employeeDashboard,
        meta: { modules: ["hrm_employee"] },
      },
      {
        path: "my-documents",
        component: employeeMyDocuments,
        meta: { requiresAuth: true },
      },
      {
        path: "deals-dashboard",
        component: dealsDashboard,
        meta: { modules: ["crm_deals"] },
      },
      {
        path: "leads-dashboard",
        component: leadsDashboard,
        meta: { modules: ["crm_leads"] },
      },
    ],
  },
  {
    path: "/icons",
    component: Icons,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/icons/icon-fontawesome" },
      { path: "icon-fontawesome", component: UI_Iconfontawesome },
      { path: "icon-feather", component: UI_Iconfeather },
      { path: "icon-ionic", component: UI_Iconionic },
      { path: "icon-material", component: UI_Iconmaterial },
      { path: "icon-pe7", component: UI_Iconpe7 },
      { path: "icon-simpleline", component: UI_Iconsimpleline },
      { path: "icon-themify", component: UI_Iconthemify },
      { path: "icon-weather", component: UI_Iconweather },
      { path: "icon-typicon", component: UI_Icontypicon },
      { path: "icon-flag", component: UI_Iconflag },
    ],
  },
  {
    path: "/baseui",
    component: BaseUi,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/baseui/ui-alerts" },
      { path: "ui-alerts", component: UI_Alerts },
      { path: "ui-accordion", component: UI_Accordion },
      { path: "ui-avatar", component: UI_Avatar },
      { path: "ui-badges", component: UI_Badges },
      { path: "ui-borders", component: UI_Borders },
      { path: "ui-buttons", component: UI_Buttons },
      { path: "ui-buttons-group", component: UI_Buttons_group },
      { path: "ui-breadcrumb", component: UI_Breadcrumb },
      { path: "ui-cards", component: UI_Cards },
      { path: "ui-carousel", component: UI_Carousel },
      { path: "ui-colors", component: UI_Colors },
      { path: "ui-dropdowns", component: UI_Dropdowns },
      { path: "ui-grid", component: UI_Grid },
      { path: "ui-images", component: UI_Images },
      { path: "ui-lightbox", component: UI_Lightbox },
      { path: "ui-media", component: UI_Media },
      { path: "ui-modals", component: UI_Modals },
      { path: "ui-offcanvas", component: UI_Offcanvas },
      { path: "ui-pagination", component: UI_Pagination },
      { path: "ui-popovers", component: UI_Popovers },
      { path: "ui-progress", component: UI_Progress },
      { path: "ui-placeholders", component: UI_Placeholders },
      { path: "ui-nav-tabs", component: Ui_Navtabs },
      { path: "ui-spinner", component: UI_Spinner },
      { path: "ui-sweetalerts", component: UI_Sweetalerts },
      { path: "ui-tooltips", component: UI_Tooltips },
      { path: "ui-typography", component: UI_Typography },
      { path: "ui-video", component: UI_Video },
    ],
  },
  {
    path: "/advancedui",
    component: Advancedui,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/advancedui/ui-ribbon" },
      { path: "ui-ribbon", component: UI_Ribbon },
      { path: "ui-clipboard", component: UI_Clipboard },
      { path: "ui-drag-drop", component: UI_Drag_Drop },
      { path: "ui-text-editor", component: UI_Text_Editor },
      { path: "ui-counter", component: UI_Counter },
      { path: "ui-scrollbar", component: UI_Scrollbar },
      { path: "ui-rating", component: UI_Rating },
      { path: "ui-stickynote", component: UI_Stickynote },
      { path: "ui-rangeslider", component: UI_Rangeslider },
      { path: "ui-timeline", component: UI_Timeline },
    ],
  },
  {
    path: "/tables",
    component: Tables,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/tables/data-tables" },
      { path: "data-tables", component: Data_Tables },
      { path: "tables-basic", component: Tables_Basic },
    ],
  },
  {
    path: "/charts",
    component: Charts,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/charts/chart-apex" },
      { path: "chart-apex", component: Chartapex },
      { path: "chart-c3", component: Chartc3 },
      { path: "chart-flot", component: Chartflot },
      { path: "chart-js", component: Chartjs },
      { path: "chart-morris", component: Chartmorris },
    ],
  },
  {
    path: "/form-elements",
    component: FormElements,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/form-elements/form-basic-inputs" },
      { path: "form-basic-inputs", component: Formbasicinput },
      { path: "form-checkbox-radios", component: Formcheckboxradios },
      { path: "form-grid-gutters", component: Formgridgutters },
      { path: "form-input-groups", component: FormInput },
      { path: "form-select", component: Formselect },
      { path: "form-mask", component: Formmask },
      { path: "form-fileupload", component: Formfileupload },
    ],
  },
  {
    path: "/form-layouts",
    component: FormLayouts,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/form-layouts/form-horizontal" },
      { path: "form-horizontal", component: FormHorizontal },
      { path: "form-vertical", component: Formvertical },
      { path: "form-floating-labels", component: Formfloatinglabels },
    ],
  },
  {
    path: "/forms",
    component: Forms,
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/forms/form-validation" },
      { path: "form-validation", component: Formvalidation },
      { path: "form-select2", component: Formselect2 },
      { path: "form-wizard", component: Formwizard },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: Error404,
  },
];
export const router = createRouter({
  history: createWebHistory("/"),
  linkActiveClass: "active",
  routes,
});

let initialLoadPending = true;
let initialRouteLoaderShown = false;
let initialLoadComplete = false;

function isGuestAuthRoute(route) {
  if (!route) return false;
  if (route.meta?.guestOnly) return true;

  const path = route.path || "";
  return (
    path.startsWith("/login") ||
    path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password") ||
    path.startsWith("/set-password") ||
    path === "/register" ||
    path.startsWith("/register-") ||
    path.startsWith("/auth/verify-email")
  );
}

function skipPremiumLoaderForNavigation(to, from) {
  return isGuestAuthRoute(to) || isGuestAuthRoute(from);
}

router.afterEach((to, from) => {
  if (to.name === "layout-rtl") {
    document.body.classList.add("layout-mode-rtl");
  } else {
    document.body.classList.remove("layout-mode-rtl");
  }

  if (!initialLoadComplete) {
    initialLoadComplete = true;
    initialLoadPending = false;
    if (!isGuestAuthRoute(to)) {
      useAppLoaderStore().finishInitialRoute();
    } else {
      useAppLoaderStore().hide();
      window.dismissBootSplash?.();
    }
  } else if (!skipPremiumLoaderForNavigation(to, from)) {
    useAppLoaderStore().endNavigation();
  } else {
    useAppLoaderStore().hide();
  }
});

// 👇 Auth navigation guard
// 🔐 Auth + Subscription/Modules navigation guard
// 🔐 Auth + Subscription/Modules + Role navigation guard
router.beforeEach(async (to, from, next) => {
  const leavingLockScreen = from.name === "lock-screen";

  if (
    from.matched.length &&
    to.path !== from.path &&
    !leavingLockScreen &&
    !skipPremiumLoaderForNavigation(to, from)
  ) {
    useAppLoaderStore().beginNavigation(to);
  }

  // Scroll to top on route change
  window.scrollTo({ top: 0, behavior: "smooth" });

  if (isTenancyFrontendEnabled() && !isErrorPageRoute(to)) {
    const centralMarketingPaths = new Set([
      "/",
      "/register",
      "/register-2",
      "/register-3",
      "/forgot-password",
      "/forgot-password-2",
      "/forgot-password-3",
      "/reset-password",
      "/set-password",
      "/workspace-not-found",
    ]);

    if (isCentralHost()) {
      if (to.path.startsWith("/login")) {
        return next({ path: "/", query: { ...to.query, workspace: "required" } });
      }
      if (
        !centralMarketingPaths.has(to.path) &&
        !to.path.startsWith("/auth/verify-email")
      ) {
        return next({ path: "/" });
      }
    }

    if (isAdminHost() && !to.path.startsWith("/super-admin") && !to.path.startsWith("/login")) {
      return next({ path: "/super-admin/dashboard" });
    }

    if (isTenantHost()) {
      if (to.name !== "workspace-not-found") {
        const check = await verifyCurrentTenantWorkspace();
        if (!check.ok) {
          return next({
            name: "workspace-not-found",
            query: check.slug ? { slug: check.slug } : {},
            replace: true,
          });
        }
      }

      if (to.path === "/" || to.path.startsWith("/register")) {
        return next({ name: "login-index", query: to.query });
      }
    }
  }

  const auth = useAuthStore();

  // Verify-email: allow onboarding token or email-confirm link before auth exists
  if (to.path === "/auth/verify-email") {
    useAppLoaderStore().hide();
    window.dismissBootSplash?.();

    const hasOnboarding = Boolean(to.query.onboarding);
    const hasEmailConfirm =
      to.query.confirm === "1" && to.query.id && to.query.hash;
    const isVerifiedCallback =
      to.query.verified === "1" || to.query.verified === "true";

    if (hasOnboarding || hasEmailConfirm || isVerifiedCallback) {
      return next();
    }

    if (!auth.isAuthenticated) {
      return next({ name: "login-index" });
    }

    return next();
  }

  const access = useAccessStore();
  let accessEnsured = false;

  const ensureAccessLoaded = async () => {
    if (accessEnsured || isSuperAdmin) return;
    await access.ensureAccess?.();
    accessEnsured = true;
  };

  // ---- ROLE HELPERS -------------------------------------------------
  const roles = auth.user?.roles || [];

  const isSuperAdmin = roles.includes("SuperAdmin");
  const isAdmin = roles.includes("Admin");
  const isHr = roles.includes("Hr") || roles.includes("HR");
  const isEmployee = roles.includes("Employee");
  const isClient = roles.includes("Client");

  // ✅ "Pure" employee = Employee only (no Admin/HR/SuperAdmin)
  const isPureEmployee = isEmployee && !isAdmin && !isHr && !isSuperAdmin;
  const isPureClient =
    isClient && !isEmployee && !isAdmin && !isHr && !isSuperAdmin;
  const hasPermission = (permission) =>
    Array.isArray(auth.permissions) && auth.permissions.includes(permission);

  // Default dashboard per role
  const safeDashboard = isSuperAdmin
    ? "/super-admin/dashboard"
    : isPureEmployee
    ? "/dashboard/employee-dashboard"
    : isPureClient
    ? "/crm/clients"
    : "/dashboard/admin-dashboard";

  const sessionLock = useSessionLockStore();
  const isLockScreenRoute = to.name === "lock-screen";

  if (!isIdleLockEnabled()) {
    if (sessionLock.isLocked) {
      sessionLock.reset();
    }
  } else if (auth.isAuthenticated && sessionLock.isLocked) {
    if (!isSuperAdmin && to.path !== "/auth/verify-email") {
      try {
        await ensureAccessLoaded();
      } catch (e) {
        console.error("Failed to load access while session locked", e);
      }
    }

    if (isLockScreenRoute) {
      return next();
    }
    // Keep the user on the current page while locked so modals/forms stay intact.
    if (from.name && to.fullPath !== from.fullPath) {
      return next(false);
    }
    return next();
  }

  if (
    isIdleLockEnabled() &&
    auth.isAuthenticated &&
    isLockScreenRoute &&
    !sessionLock.isLocked
  ) {
    const redirectTarget =
      typeof to.query.redirect === "string" && to.query.redirect.startsWith("/")
        ? to.query.redirect
        : safeDashboard;
    return next({ path: redirectTarget, replace: true });
  }

  // ---- AUTH REQUIREMENT ---------------------------------------------
  const requiresModuleAccess = to.matched.some(
    (record) => record.meta && record.meta.modules,
  );
  const requiresSuperAdmin = to.matched.some(
    (record) => record.meta && record.meta.superAdminOnly,
  );
  const requiresAdminOnly = to.matched.some(
    (record) => record.meta && record.meta.adminOnly,
  );

  const requiresAuth =
    to.matched.some((record) => record.meta && record.meta.requiresAuth) ||
    requiresModuleAccess;

  // 1) Not authenticated but route requires it -> send to login
  if (requiresAuth && !auth.isAuthenticated) {
    return next({ name: "login-index" });
  }

  if (requiresSuperAdmin && !isSuperAdmin) {
    return next({ path: safeDashboard, replace: true });
  }

  if (requiresAdminOnly && !roles.includes("Admin")) {
    return next({ path: safeDashboard, replace: true });
  }

  // From here on, user is either:
  // - authenticated, or
  // - route does not require auth.

  // ---- SHORT-CIRCUIT: already logged in & going to auth pages ------
  // ---- BLOCK AUTH PAGES FOR LOGGED-IN USERS ------------------------
  if (auth.isAuthenticated) {
    const isGuestOnlyRoute = to.matched.some(
      (record) => record.meta && record.meta.guestOnly,
    );

    if (isGuestOnlyRoute) {
      // ⛔ Prevent back-button returning to login
      return next({ path: safeDashboard, replace: true });
    }
  }

  if (auth.isAuthenticated && !auth.user?.email_verified_at) {
    const verificationAllowedPaths = ["/auth/verify-email"];
    if (!verificationAllowedPaths.includes(to.path)) {
      return next({
        path: "/auth/verify-email",
        query: to.path === "/select-plan" ? to.query : undefined,
      });
    }
  }

  if (
    auth.isAuthenticated &&
    requiresAuth &&
    !isSuperAdmin &&
    to.path !== "/auth/verify-email"
  ) {
    try {
      await ensureAccessLoaded();

      if (access.status === "locked" && !["/renewal-required", "/billing"].includes(to.path)) {
        return next({ path: "/renewal-required" });
      }

      if (
        ["Suspended", "Inactive"].includes(access.companyStatus) &&
        !["/company-suspended", "/billing", "/renewal-required"].includes(to.path)
      ) {
        return next({ path: "/company-suspended" });
      }
    } catch (e) {
      console.error("Failed to load access status", e);
      return next({ name: "error-500" });
    }
  }

  // ---- ROLE-BASED DASHBOARD HANDLING -------------------------------

  if (auth.isAuthenticated) {
    // Hitting bare /dashboard → send to role-appropriate dashboard
    if (to.path === "/dashboard" || to.path === "/dashboard/") {
      if (to.path !== safeDashboard) {
        return next(safeDashboard);
      }
    }

    if (isPureClient && to.path.startsWith("/dashboard")) {
      return next(safeDashboard);
    }

    // Employee should never land on Admin dashboard
    if (to.path === "/dashboard/admin-dashboard" && isPureEmployee) {
      return next("/dashboard/employee-dashboard");
    }

    // Admin / HR / SuperAdmin should not use Employee dashboard
    if (to.path === "/dashboard/employee-dashboard" && !isPureEmployee) {
      return next("/dashboard/admin-dashboard");
    }
  }

  // ---- EMPLOYEE vs ADMIN/HR ROUTE RESTRICTIONS ---------------------

  if (auth.isAuthenticated) {
    if (isPureClient) {
      const clientAllowedBasePrefixes = [
        "/notifications",
        "/pages/search-result",
        "/company-suspended",
        "/renewal-required",
        "/billing",
      ];

      const clientPermissionRoutes = [
        {
          matches: (path) =>
            path === "/crm/clients" ||
            path === "/crm/clients-grid" ||
            path.startsWith("/crm/clients-details/"),
          permission: "Client.Read",
        },
        {
          matches: (path) =>
            path === "/crm/contacts" ||
            path === "/crm/contacts-grid" ||
            path.startsWith("/crm/contacts-details/"),
          permission: "Contact.Read",
        },
        {
          matches: (path) =>
            path === "/projects/projects-list" ||
            path === "/projects/projects-grid" ||
            path.startsWith("/projects/projects-details/"),
          permission: "Project.Read",
        },
        {
          matches: (path) =>
            path === "/projects/tasks" || path === "/projects/task-board",
          permission: "Task.Read",
        },
        {
          matches: (path) =>
            path === "/sales/invoices" || /^\/sales\/invoices\/\d+$/.test(path),
          permission: "Invoice.Read",
        },
        {
          matches: (path) => path === "/sales/payments",
          permission: "Payment.Read",
        },
      ];

      const matchedClientRoute = clientPermissionRoutes.find((entry) =>
        entry.matches(to.path)
      );

      const isAllowedClientRoute =
        clientAllowedBasePrefixes.some((prefix) => to.path.startsWith(prefix)) ||
        !!matchedClientRoute;

      if (!isAllowedClientRoute) {
        return next(safeDashboard);
      }

      if (matchedClientRoute && !hasPermission(matchedClientRoute.permission)) {
        return next(safeDashboard);
      }
    }

    // 1) Admin/HR should NOT see employee-only self-service pages
    const employeeSelfServicePaths = [
      "/attendance/attendance-employee",
      "/leave/leaves-employee",
      "/dashboard/my-documents",
      "/payroll/my-payslips",
      "/payroll/my-p9",
    ];

    if (!isPureEmployee && employeeSelfServicePaths.includes(to.path)) {
      return next(safeDashboard);
    }

    // 2) Employees should NOT see Applications → Invoices
    const applicationInvoicePaths = [
      "/applications/invoices",
      "/applications/add-invoices",
      "/applications/edit-invoices",
      "/applications/invoice-details",
    ];

    if (isPureEmployee && applicationInvoicePaths.includes(to.path)) {
      // You can also redirect to "/applications/chat" if you prefer
      return next(safeDashboard);
    }

    if (isPureEmployee && to.path.startsWith("/crm")) {
      const isCrmRoot = to.path === "/crm" || to.path === "/crm/";
      if (!isCrmRoot && !canAccessCrmRoute(to.path, hasPermission)) {
        return next(safeDashboard);
      }
    }

    const canAccessPayrollAdmin =
      isSuperAdmin ||
      isAdmin ||
      isHr ||
      hasPermission("Payroll.Read") ||
      hasPermission("Payroll.Create") ||
      hasPermission("Payroll.Edit") ||
      hasPermission("Payroll.Approve");

    const payrollAdminPaths = [
      "/payroll/employee-salary",
      "/payroll/payroll",
      "/payroll/payroll-overtime",
      "/payroll/payroll-overtime-entries",
      "/payroll/payroll-deduction",
      "/payroll/payroll-settings",
      "/payroll/bank-transfer-formats",
      "/payroll/payroll-meta-conversion",
      "/payroll/payroll-adjustments",
    ];

    const payrollReportPaths = [
      "/reports/payslip-report",
      "/reports/cash-payroll-report",
      "/reports/cash-payroll-audit-report",
      "/reports/check-payroll-report",
      "/reports/check-payroll-audit-report",
      "/reports/payroll-control-totals",
      "/reports/comparative-report",
      "/reports/imported-payroll-history-report",
      "/reports/bank-transfer-report",
      "/reports/statutory-deduction-report",
      "/reports/p9-report",
      "/reports/legacy-p9-report",
      "/reports/muster-roll-report",
    ];

    if (
      !isPureEmployee &&
      (payrollAdminPaths.includes(to.path) ||
        payrollReportPaths.includes(to.path)) &&
      !canAccessPayrollAdmin
    ) {
      return next(safeDashboard);
    }

    if (!isClient && to.path.startsWith("/sales")) {
      const rule = matchFinanceSalesRoute(to.path);
      if (
        rule &&
        !isSuperAdmin &&
        !isAdmin &&
        !isHr &&
        !hasPermission(rule.permission)
      ) {
        return next(safeDashboard);
      }
    }

    if (!isClient && to.path.startsWith("/accounting")) {
      const rule = matchFinanceAccountingRoute(to.path);
      if (
        rule &&
        !isSuperAdmin &&
        !isAdmin &&
        !isHr &&
        !hasPermission(rule.permission)
      ) {
        return next(safeDashboard);
      }
    }

    if (isPureEmployee && to.path.startsWith("/asset")) {
      if (!canAccessAdminAssetsRoute(to.path, hasPermission)) {
        return next(safeDashboard);
      }
    }

    if (isPureEmployee && to.path.startsWith("/user-management")) {
      if (!canAccessAdminSettingsRoute(to.path, hasPermission)) {
        return next(safeDashboard);
      }
    }

    if (isPureEmployee && to.path.startsWith("/reports")) {
      if (!canAccessAdminReportsRoute(to.path, hasPermission)) {
        return next(safeDashboard);
      }
    }

    const adminSettingsPrefixes = [
      "/general-settings",
      "/website-settings",
      "/app-settings",
      "/system-settings",
      "/financial-settings",
      "/others-settings",
    ];
    if (
      isPureEmployee &&
      adminSettingsPrefixes.some((prefix) => to.path.startsWith(prefix))
    ) {
      if (!canAccessAdminSettingsRoute(to.path, hasPermission)) {
        return next(safeDashboard);
      }
    }

    const hrmRoutePrefixes = [
      "/employee/",
      "/payroll/",
      "/attendance/",
      "/leave/",
      "/performance/",
      "/training/",
      "/hrm/",
      "/tickets/",
    ];
    if (
      isPureEmployee &&
      hrmRoutePrefixes.some((prefix) => to.path.startsWith(prefix))
    ) {
      if (!canAccessHrmRoute(to.path, hasPermission, { isPureEmployee })) {
        return next(safeDashboard);
      }
    }
  }

  // ---- MODULE / PLAN ACCESS (KEEPING YOUR EXISTING LOGIC) ----------

  // if (requiresModuleAccess) {
  //   try {
  //     // ✅ FIX: Always ensure access is loaded before checking modules.
  //     // This prevents the refresh race where `modules=[]` temporarily.
  //     if (typeof access.ensureAccess === "function") {
  //       await access.ensureAccess();
  //     } else {
  //       // fallback if ensureAccess not available for some reason
  //       if (!access.loaded) {
  //         await access.fetchAccess();
  //       }
  //     }

  //     // Trial/subscription ended -> redirect to pricing / upgrade page
  //     if (access.status === "expired") {
  //       return next({ path: "/pages/pricing" });
  //     }

  //     const requiredModules = to.matched
  //       .map((record) => record.meta.modules)
  //       .filter(Boolean)
  //       .flat();

  //     const hasSomeModule = requiredModules.some((m) =>
  //       access.modules.includes(m)
  //     );

  //     if (!hasSomeModule) {
  //       // ❗ No permission for this route -> send to role-based dashboard
  //       return next({ path: safeDashboard });
  //     }
  //   } catch (e) {
  //     console.error("Failed to load access/modules", e);
  //     // Fallback: go to error page
  //     return next({ name: "error-500" });
  //   }
  // }

  if (auth.isAuthenticated && (to.path === "/crm" || to.path === "/crm/")) {
    try {
      await ensureAccessLoaded();

      const landing = resolveCrmLanding(access.modules, hasPermission, {
        fullAccess: isSuperAdmin || access.fullAccess,
        requirePermission: isPureEmployee,
      });

      return next({ path: landing || safeDashboard, replace: true });
    } catch (e) {
      console.error("Failed to resolve CRM landing route", e);
      return next({ path: safeDashboard, replace: true });
    }
  }

  // ---- MODULE / PLAN ACCESS ----------
  if (requiresModuleAccess) {
    try {
      if (isSuperAdmin) {
        return next();
      }

      await ensureAccessLoaded();

      if (access.status === "locked") {
        return next({ path: "/renewal-required" });
      }

      if (access.status === "expired") {
        return next({ path: "/billing" });
      }

      // ✅ ONLY enforce modules from the deepest matched route that defines meta.modules
      const matchedWithModules = to.matched.filter(
        (r) => Array.isArray(r.meta?.modules) && r.meta.modules.length,
      );

      const requiredModules = matchedWithModules.length
        ? matchedWithModules[matchedWithModules.length - 1].meta.modules
        : [];

      const allowed =
        access.fullAccess ||
        requiredModules.some((m) => access.modules.includes(m));

      if (!allowed) {
        return next({ path: safeDashboard });
      }
    } catch (e) {
      console.error("Failed to load access/modules", e);
      return next({ name: "error-500" });
    }
  }

  // Default: allow navigation
  if (
    initialLoadPending &&
    !initialRouteLoaderShown &&
    from.name !== "lock-screen" &&
    !isGuestAuthRoute(to)
  ) {
    initialRouteLoaderShown = true;
    useAppLoaderStore().showInitialRoute(to);
  } else if (initialLoadPending && isGuestAuthRoute(to)) {
    initialRouteLoaderShown = true;
    useAppLoaderStore().hide();
    window.dismissBootSplash?.();
  }

  return next();
});

export default router;
